import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Chip
} from '@mui/material';
import { format } from 'date-fns';
import { Review, TrendData } from '../../types/review';
import { reviewApi } from '../../api/reviews';
import EnhancedReviewForm from './EnhancedReviewForm';
import TrendCard from './TrendCard';

interface SimpleReviewHistoryProps {
  clientId: string;
}

const SimpleReviewHistory: React.FC<SimpleReviewHistoryProps> = ({ clientId }) => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [reviewDue, setReviewDue] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isReviewDialogOpen, setIsReviewDialogOpen] = useState(false);
  const [trendData, setTrendData] = useState<TrendData | null>(null);

  useEffect(() => {
    loadReviews();
    loadTrendData();
  }, [clientId]);

  const loadReviews = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('=== LOADING REVIEWS ===');
      console.log('Client ID:', clientId);
      
      let response;
      try {
        response = await reviewApi.getClientReviews(clientId);
        console.log('Raw response received:', response);
        console.log('Response type:', typeof response);
        console.log('Response keys:', response ? Object.keys(response) : 'null/undefined');
      } catch (apiError: any) {
        console.error('API call failed:', apiError);
        console.error('Error details:', apiError.message, apiError.code, apiError.details);
        throw apiError;
      }
      
      // The baseApi interceptor already unwraps response.data
      // So we receive the data object directly: { reviews: Review[], reviewDue: boolean }
      if (response && typeof response === 'object' && 'reviews' in response) {
        console.log('✓ Valid response format with reviews property');
        console.log('Reviews array:', response.reviews);
        console.log('Number of reviews:', response.reviews?.length);
        console.log('Review due:', response.reviewDue);
        setReviews(response.reviews || []);
        setReviewDue(response.reviewDue || false);
      } else if (Array.isArray(response)) {
        console.log('✓ Array format (legacy)');
        setReviews(response);
        setReviewDue(false);
      } else {
        console.error('✗ Unexpected response format:', response);
        console.error('Response is null/undefined or wrong type');
        setReviews([]);
        setReviewDue(false);
      }
    } catch (err: any) {
      console.error('=== ERROR IN LOADREVIEWS ===');
      console.error('Error object:', err);
      console.error('Error message:', err.message);
      console.error('Error stack:', err.stack);
      setError(err.message || 'Failed to load review history');
      setReviews([]);
    } finally {
      setLoading(false);
      console.log('=== LOADREVIEWS COMPLETE ===');
    }
  };

  const handleOpenReviewDialog = () => {
    setIsReviewDialogOpen(true);
  };

  const handleCloseReviewDialog = () => {
    setIsReviewDialogOpen(false);
  };

  const handleReviewSaved = () => {
    handleCloseReviewDialog();
    loadReviews();
    loadTrendData();
  };

  const loadTrendData = async () => {
    try {
      const trends = await reviewApi.getTrendData(clientId);
      setTrendData(trends);
    } catch (err) {
      console.warn('Could not load trend data:', err);
    }
  };

  const getCounsellorName = (review: Review): string => {
    if (typeof review.counsellorId === 'object') {
      return `${review.counsellorId.firstName} ${review.counsellorId.lastName}`;
    }
    return 'Unknown Counsellor';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Mental Health Trends Section */}
      {trendData && (trendData.initialAssessment || trendData.previousReview) && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" className="theme-text-primary" sx={{ mb: 2, fontWeight: 600 }}>
            Mental Health Trends
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TrendCard
              title="PHQ-9 (Depression)"
              initialScore={trendData.initialAssessment?.phq9Total}
              previousScore={trendData.previousReview?.phq9Total}
              maxScore={27}
            />
            <TrendCard
              title="GAD-7 (Anxiety)"
              initialScore={trendData.initialAssessment?.gad7Total}
              previousScore={trendData.previousReview?.gad7Total}
              maxScore={21}
            />
          </Box>
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" className="theme-text-primary">
          Review History
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {reviewDue && (
            <Chip 
              label="Review Due" 
              color="warning" 
              sx={{ fontWeight: 'bold' }}
            />
          )}
          <Button
            variant="contained"
            onClick={handleOpenReviewDialog}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              backgroundColor: '#f59e0b',
              '&:hover': {
                backgroundColor: '#d97706'
              }
            }}
          >
            Complete Review
          </Button>
        </Box>
      </Box>

      {reviews.length === 0 ? (
        <Alert 
          severity="info"
          sx={{
            bgcolor: 'rgba(33, 150, 243, 0.1)',
            color: '#64b5f6',
            border: '1px solid rgba(33, 150, 243, 0.3)'
          }}
        >
          No reviews have been completed yet. Reviews are completed every 6-8 sessions.
        </Alert>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {reviews.map((review) => (
            <div key={review._id} className="glass-card p-6">
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold" className="theme-text-primary">
                    Session #{review.sessionNumber} Review
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    {format(new Date(review.reviewDate), 'dd MMMM yyyy')}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                    Counsellor: {getCounsellorName(review)}
                  </Typography>
                </Box>
              </Box>

              {/* Mental Health Scores */}
              {(review.phq9Total !== undefined || review.gad7Total !== undefined) && (
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  {review.phq9Total !== undefined && (
                    <Box sx={{ flex: 1, p: 2, bgcolor: 'rgba(102, 126, 234, 0.1)', borderRadius: 1, border: '1px solid rgba(102, 126, 234, 0.3)' }}>
                      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontWeight: 600 }}>
                        PHQ-9 (Depression)
                      </Typography>
                      <Typography variant="h5" className="theme-text-primary" sx={{ fontWeight: 700 }}>
                        {review.phq9Total}
                        <Typography component="span" variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', ml: 0.5 }}>
                          / 27
                        </Typography>
                      </Typography>
                    </Box>
                  )}
                  {review.gad7Total !== undefined && (
                    <Box sx={{ flex: 1, p: 2, bgcolor: 'rgba(118, 75, 162, 0.1)', borderRadius: 1, border: '1px solid rgba(118, 75, 162, 0.3)' }}>
                      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontWeight: 600 }}>
                        GAD-7 (Anxiety)
                      </Typography>
                      <Typography variant="h5" className="theme-text-primary" sx={{ fontWeight: 700 }}>
                        {review.gad7Total}
                        <Typography component="span" variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', ml: 0.5 }}>
                          / 21
                        </Typography>
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              {/* Needs Being Met */}
              <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(255, 255, 255, 0.05)', borderRadius: 1, border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                <Typography variant="body2" fontWeight="bold" gutterBottom className="theme-text-primary">
                  Are the client's needs being met?
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                  {review.needsBeingMet}
                </Typography>
              </Box>

              {/* Progress Towards Objectives */}
              {review.progressTowardsObjectives && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(255, 255, 255, 0.05)', borderRadius: 1, border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                  <Typography variant="body2" fontWeight="bold" gutterBottom className="theme-text-primary">
                    Progress Towards Objectives
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                    {review.progressTowardsObjectives}
                  </Typography>
                </Box>
              )}

              {/* Recommended Next Steps */}
              {review.recommendedNextSteps && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(255, 255, 255, 0.05)', borderRadius: 1, border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                  <Typography variant="body2" fontWeight="bold" gutterBottom className="theme-text-primary">
                    Recommended Next Steps
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', textTransform: 'capitalize' }}>
                    {review.recommendedNextSteps.replace('_', ' ')}
                  </Typography>
                  {review.nextStepsNotes && (
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mt: 1, fontStyle: 'italic' }}>
                      {review.nextStepsNotes}
                    </Typography>
                  )}
                </Box>
              )}

              {/* Additional Notes */}
              {review.additionalNotes && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(255, 255, 255, 0.05)', borderRadius: 1, border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                  <Typography variant="body2" fontWeight="bold" gutterBottom className="theme-text-primary">
                    Additional Notes
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                    {review.additionalNotes}
                  </Typography>
                </Box>
              )}
            </div>
          ))}
        </Box>
      )}

      {/* Review Dialog */}
      <Dialog
        open={isReviewDialogOpen}
        onClose={handleCloseReviewDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(30, 41, 59, 0.95)',
            backdropFilter: 'blur(20px)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '16px'
          }
        }}
      >
        <DialogTitle sx={{ pb: 2 }}>
          <Typography 
            variant="h6" 
            className="theme-text-primary"
            sx={{ 
              fontFamily: 'Roboto', 
              fontWeight: 600,
              fontSize: '1.25rem'
            }}
          >
            Complete Client Review
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <EnhancedReviewForm
            clientId={clientId}
            onSave={handleReviewSaved}
            onCancel={handleCloseReviewDialog}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default SimpleReviewHistory;
